/**
 * Unified Locale Cookie Utilities
 *
 * Provides SSR-safe cookie operations for locale storage.
 * Uses the NEXT_LOCALE cookie to ensure consistency between
 * react-i18next and LocaleContext systems.
 */

import Cookies from "js-cookie";

const LOCALE_COOKIE_KEY = "NEXT_LOCALE";
const COOKIE_MAX_AGE = 365; // days

export type LocaleCode = "en" | "fr" | "fr-CA";

/**
 * Maps between different locale formats used by the two systems
 */
export const LOCALE_MAPPING = {
    en: "en",
    fr: "fr-CA", // LocaleContext "fr" maps to i18next "fr-CA"
    "fr-CA": "fr-CA",
} as const;

/**
 * Reverse mapping for LocaleContext (uses simplified "fr")
 */
export const LOCALE_CONTEXT_MAPPING = {
    en: "en",
    "fr-CA": "fr", // i18next "fr-CA" maps to LocaleContext "fr"
} as const;

/**
 * Gets the stored locale from NEXT_LOCALE cookie
 */
export function getStoredLocale(): string | null {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        return Cookies.get(LOCALE_COOKIE_KEY) || null;
    } catch (error) {
        console.warn("Failed to read locale from cookie:", error);
        return null;
    }
}

/**
 * Saves locale to NEXT_LOCALE cookie
 */
export function setStoredLocale(locale: string): void {
    if (typeof window === "undefined") {
        return;
    }

    try {
        Cookies.set(LOCALE_COOKIE_KEY, locale, {
            path: "/",
            expires: COOKIE_MAX_AGE,
        });

        // Dispatch custom event for cross-component synchronization
        window.dispatchEvent(new CustomEvent("localeChange", { detail: { locale } }));
    } catch (error) {
        console.warn("Failed to save locale to cookie:", error);
    }
}

/**
 * Clears the locale cookie
 */
export function clearStoredLocale(): void {
    if (typeof window === "undefined") {
        return;
    }

    try {
        Cookies.remove(LOCALE_COOKIE_KEY, { path: "/" });
    } catch (error) {
        console.warn("Failed to clear locale cookie:", error);
    }
}

/**
 * Normalizes locale to i18next format (en or fr-CA)
 */
export function normalizeLocaleForI18n(locale: string): string {
    return LOCALE_MAPPING[locale as keyof typeof LOCALE_MAPPING] || "en";
}

/**
 * Normalizes locale to LocaleContext format (en or fr)
 */
export function normalizeLocaleForContext(locale: string): "en" | "fr" {
    return (LOCALE_CONTEXT_MAPPING[locale as keyof typeof LOCALE_CONTEXT_MAPPING] || "en") as
        | "en"
        | "fr";
}

/**
 * Server-side cookie reading (for Next.js server components)
 */
export function getServerLocale(cookieHeader?: string): string | null {
    if (!cookieHeader) return null;

    const match = cookieHeader.match(/NEXT_LOCALE=([^;]+)/);
    return match ? match[1] : null;
}
